import * as angular from "angular";
import * as moment from "moment";

import { IImportConfig } from "./interfaces/IImportConfig";
import { IImportCount } from "./interfaces/IImportCount";
import { IImportOption } from "./interfaces/IImportOption";
import { IImportAdapterResult, ImportPreProcessResult, IRowCol } from "./interfaces/IImportAdapterResult";
import { IImportResult } from "./interfaces/IImportResult";
import { IImportValidationError, IImportValidationStatus } from "./interfaces/IImportValidation";
import { IVeriTableHeader, IVeriTableRow } from "@directives/_veri/veriTable/veriTable.component";

import { FieldType } from "./enums/FieldType";
import { CsvImporterService } from "./csvImporter.service";
import { FileReaderService } from "@services/fileReader.service";
import { ImportSource } from "./enums/ImportSource";

import "./csvImporter.html";
import "./csvImporter.scss";
import { TranslationService } from "@services/translation.service";

export interface ICsvImportComponentBindings {
    importConfig: IImportConfig;
    loading: boolean;
    onCancel: () => void;
    onSubmit: (importedData: any) => angular.IPromise<IImportCount>;
    onValidateRow: (args: { csvLine: string, importOptions: IImportOption[], rowIdx: number }) => IImportValidationStatus;
    onValidateData: (args: { csvLines: string[], importOptions: IImportOption[] }) => angular.IPromise<IVeriTableRow<IImportResult>[]>;
    onUseAdapter: (args: { csvLines: string[] }) => boolean;
    onCallAdapter: (args: { csvLines: string[], startOfWeekDate: Date }) => IImportAdapterResult;
}

interface IDateForm {
    startOfWeekDate: Date;
}

export class CsvImporterController implements ICsvImportComponentBindings {
    importConfig: IImportConfig;
    loading: boolean;
    onCancel: () => void;
    onSubmit: (importedData: any) => angular.IPromise<IImportCount>;
    onValidateRow: (args: { csvLine: string, importOptions: IImportOption[], rowIdx: number }) => IImportValidationStatus;
    onValidateData: (args: { csvLines: string[], importOptions: IImportOption[] }) => angular.IPromise<IVeriTableRow<IImportResult>[]>;
    onUseAdapter: (args: { csvLines: string[] }) => boolean;
    onCallAdapter: (args: { csvLines: string[], startOfWeekDate: Date }) => IImportAdapterResult;

    mappedImportOptions: IImportOption[];
    importOptions: IImportOption[];
    tableHeaders: IVeriTableHeader[];
    informationText: string;

    importFile: File;
    importedData: any[];
    invalidRouteCount = 0;
    inputId: string;

    showMappings = false;
    showResults = false;
    mappingsIncomplete = true;
    showRowErrors = false;
    showValidationErrors = false;
    showSelectDate = false;
    showHelpInfo = false;

    validationErrors: IImportValidationError[];

    submitted = false;
    successCount: number;
    failedCount: number;

    helpInfoText: string;

    columnImportNames: string[] = [];

    tableRows: IVeriTableRow<any>[] = [];

    repeatOption: IImportOption;

    csvImportLines: string[];
    infoTableHeaderCols: string[];
    infoTableRowCols: string[];

    dateForm: IDateForm;
    minDate: Date;
    maxDate: Date;

    maxCols = 0;

    static $inject = [
        "$mdDialog", "csvImporterService", "fileReaderService", "translationService"
    ];

    constructor(
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly csvImporterService: CsvImporterService,
        private readonly fileReaderService: FileReaderService,
        private readonly translationService: TranslationService
    )
    {
        this.inputId = `fileinputid-${Math.floor(Math.random() * 1000)}`;
    }

    $onInit() {
        this.submitted = false;
        this.successCount = 0;
        this.failedCount = 0;
        this.validationErrors = [] as IImportValidationError[];
        this.importOptions = this.importConfig.importOptions;
        this.tableHeaders = this.importConfig.tableHeaders;
        this.informationText = this.importConfig.informationText;
        this.setHelpInfoText();
        this.infoTableHeaderCols = this.importConfig.informationTableHeader.split(",");
        this.infoTableRowCols = this.importConfig.informationTableRow.split(",");
        this.dateForm = { startOfWeekDate: null };
        this.minDate = this.getMinStartOfWeekDate();
        this.maxDate = new Date(this.minDate.getFullYear() + 2, this.minDate.getMonth(), this.minDate.getDate());
    }

    importFileArrived(file: File) {
        if (this.importFile || this.loading) {
            return;
        }
        this.submitted = false;
        this.showHelpInfo = false;
        this.successCount = 0;
        this.failedCount = 0;
        this.validationErrors = [] as IImportValidationError[];
        this.importFile = file;

        if (this.importFile) {
            this.fileReaderService
                .readAsText(this.importFile)
                .then(fileText => {
                    this.prepareToProcessCsvFile(fileText);
                });
        }
    }

    back() {
        this.clear();
    }

    cancel(): void {
        this.clear();
        if (this.onCancel) {
            this.onCancel();
        }
       
        this.$mdDialog.hide();
    }

    cancelValidationErrors() {
        this.clear();
        if (this.onCancel) {
            this.onCancel();
        }
        this.showValidationErrors = false;
    }

    submit(): void {
        if (!this.onSubmit) {
            return;
        }

        this.onSubmit({ importedData: this.importedData }).then((result => {
            this.successCount = result.successCount;
            this.failedCount = result.failedCount;
            this.submitted = true;
        }));
    }

    getFieldType(fieldType: FieldType): string {
        return FieldType[fieldType];
    }

    helpInfo() {
        this.showHelpInfo = !this.showHelpInfo;
        this.setHelpInfoText();
    }

    onlySundayDatePredicate(date) {
        return date.getDay() === 0;
    };

    onDateChanged() {
        this.showSelectDate = false;
        this.preprocessImportFile(new Date(this.dateForm.startOfWeekDate));
        this.dateForm.startOfWeekDate = null;
    }

    private setHelpInfoText() {
        this.helpInfoText = this.showHelpInfo ? this.translationService.t("csvImporter.hideHelp") : this.translationService.t("csvImporter.showHelp");
    }

    private clear(): void {
        this.importedData = [];
        this.tableRows = [];
        this.validationErrors = [];
        this.importFile = null;
        this.invalidRouteCount = 0;
        this.successCount = 0;
        this.failedCount = 0;
        this.showResults = false;
        this.showSelectDate = false;
        this.showValidationErrors = false;
        this.showMappings = false;
    }

    private prepareToProcessCsvFile(fileText: string) {
        this.csvImportLines = this.csvImporterService.extractLines(fileText);

        if (this.onUseAdapter && this.onCallAdapter && this.onUseAdapter({ csvLines: this.csvImportLines })) {
            this.showSelectDate = true;
        }
        else {
            this.processCsvFile(this.csvImportLines);
        }
    }

    private preprocessImportFile(startOfWeekDate: Date) {
        const conversionResult = this.onCallAdapter({ csvLines: this.csvImportLines, startOfWeekDate: startOfWeekDate });

        if (conversionResult.status === ImportPreProcessResult.Ok && conversionResult.preProcessedDataRows && conversionResult.preProcessedDataRows.length) {
            this.processAdaptedImportFile(conversionResult);
        } else {
            this.preparePreprocessValidationErrors(conversionResult);
        }
    }

    private preparePreprocessValidationErrors(conversionResult: IImportAdapterResult) {
        const errors = conversionResult.errors;
        if (errors && errors.length) {
            errors.forEach((error: IImportValidationError) => {
                const colNumber = error.colNumber ? + error.colNumber : null;
                this.csvImporterService.addError(this.validationErrors, error.fieldName, error.fieldValue, error.text, error.rowNumber, conversionResult.originalRowCols, colNumber);
            });
        } else {
            this.csvImporterService.addError(this.validationErrors, "Generic", "unknown", this.translationService.t("csvImporter.unrecognizedParagon"), 1, conversionResult.originalRowCols);
        }
        this.displayValidationErrors();
    }

    private processAdaptedImportFile(conversionResult: IImportAdapterResult) {
        this.initOptions(false);

        this.processCsvDataUseColumnPositions(conversionResult.preProcessedDataRows, conversionResult.originalRowCols);
    }

    private processCsvFile(csvLines: string[]) {
        const hasTitles = this.csvFileHasTitles(csvLines);

        this.initOptions(hasTitles);

        if (hasTitles) {
            this.processCsvDataUseTitles(csvLines);
        } else {
            this.processCsvDataUseColumnPositions(csvLines, null);
        }
    }

    private initOptions(hasTitles: boolean) {
        this.createMappedOptionsArray();

        this.mappedImportOptions.forEach(option => {
            option.isValid = true;
            option.mappedColumnIdx = hasTitles ? -1 : option.indexPosition;
            option.repeatCount = 0;
        });

        this.repeatOption = this.mappedImportOptions.find(opt => opt.repeats);
    }

    private createMappedOptionsArray() {
        if (this.mappedImportOptions) {
            this.mappedImportOptions.length = 0;
        }
        this.mappedImportOptions = angular.copy(this.importOptions);
    }

    private csvFileHasTitles(csvLines: string[]): boolean {
        const titleRowCellValues = this.csvImporterService.getTitleRowCellValues(csvLines);

        if (!titleRowCellValues || !titleRowCellValues.length) {
            return false;
        }

        for (let cell of titleRowCellValues) {
            if (cell && cell.trim().length > 0) {
                if (this.csvImporterService.isValidFieldForType(cell, FieldType.Date).isValid ||
                    this.csvImporterService.isValidFieldForType(cell, FieldType.Integer).isValid ||
                    this.csvImporterService.isValidFieldForType(cell, FieldType.PositiveDouble).isValid) {
                    return false;
                }
            }
        }

        return true;
    }

    private removeHeaderLine(csvLines: string[]): string[] {
        csvLines.splice(0, 1);
        return csvLines;
    }

    private getMappedImportOption(colIdx: number): IImportOption {
        const importOption = this.mappedImportOptions.find(opt => opt.mappedColumnIdx === colIdx);
        if (importOption) {
            return importOption;
        }

        if (!this.repeatOption || colIdx < this.repeatOption.mappedColumnIdx || !this.repeatOption.repeatAfterCols || this.repeatOption.repeatAfterCols < 1) {
            return null;
        }

        const repeatColIdx = this.repeatOption.mappedColumnIdx;
        const repeatAfterCols = this.repeatOption.repeatAfterCols;

        const offsetCol = (colIdx - repeatColIdx) % repeatAfterCols;
        const effectiveColIdx = repeatColIdx + offsetCol;

        return this.mappedImportOptions.find(opt => opt.mappedColumnIdx === effectiveColIdx);
    }

    private processCsvDataUseTitles(csvLines: string[]) {
        this.setTitleOptions(csvLines);

        if (this.validationErrors.length) {
            this.displayValidationErrors();
            return;
        }

        const lines = this.removeHeaderLine(csvLines);
        this.processCsvImportData(lines, null);
    }

    private processCsvDataUseColumnPositions(csvLines: string[], rowColTranslations: IRowCol[][]) {
        this.processCsvImportData(csvLines, rowColTranslations);
        this.getDropdownColumnNumbers(this.maxCols);
    }

    private processCsvImportData(csvLines: string[], rowColTranslations: IRowCol[][]) {
        this.maxCols = 0;

        let allRowsValid = true;
        let rowIsValid = false;

        this.tableRows = [];

        csvLines.forEach((csvLine, rowIdx) => {
            rowIsValid = this.validateCellFormats(csvLine, rowIdx, rowColTranslations);

            rowIsValid = rowIsValid && this.validateRow(csvLine, rowIdx);

            allRowsValid = allRowsValid && rowIsValid;
        });

        if (allRowsValid) {
            this.validateAllRows(csvLines).then((tableRows: IVeriTableRow<IImportResult>[]) => {
                this.tableRows = angular.copy(tableRows);
                this.invalidRouteCount = tableRows.filter(x => !x.data.isValid).length;
                tableRows.length = 0;
                if (this.invalidRouteCount) {
                    this.displayValidationErrors();
                }
            });
        } else {
            this.displayValidationErrors();
        }
    }

    private validateRow(csvLine: string, rowIdx: number): boolean {

        if (!this.onValidateRow) {
            return true;
        }

        const validationStatus = this.onValidateRow({
            csvLine: csvLine,
            importOptions: this.mappedImportOptions,
            rowIdx: rowIdx
        });

        if (!validationStatus) {
            return true;
        }

        if (!validationStatus.isValid && validationStatus.error) {
            this.validationErrors.push(validationStatus.error);
        }

        return validationStatus.isValid;
    }

    private validateAllRows(csvLines: string[]): angular.IPromise<IVeriTableRow<IImportResult>[]> {
        this.importedData = [] as any[];

        if (!this.onValidateData) {
            return null;
        }

        return this.onValidateData({
            csvLines: csvLines,
            importOptions: this.mappedImportOptions
        })
        .then((importResults: IVeriTableRow<IImportResult>[]) => {
            importResults.forEach((importResult, rowIdx) => {
                const validationName = this.importConfig.importSource === ImportSource.RouteTemplate ? this.translationService.t("csvImporter.sitesData") : this.translationService.t("csvImporter.geofenceData");
                this.validateImportResult(importResult, rowIdx, validationName);
                this.importedData.push(angular.copy(importResult.data));
            });
            return importResults;
        });
    }

    private validateImportResult(routeResult: IVeriTableRow<IImportResult>, rowIdx: number, genericErrorText: string) {
        if (!routeResult.data.isValid && routeResult.data.errors.length) {
            const errorText = routeResult.data.errors.join(", ").replace(/, *$/, "");
            this.addOrUpdateValidationError(genericErrorText, routeResult.data.altName, errorText, rowIdx, null);
        }
    }

    private validateCellFormats(csvLine: string, rowIdx: number, rowColTranslations: IRowCol[][]): boolean {

        const cellValues = this.csvImporterService.getFieldValuesFromLine(csvLine);

        let rowIsValid = cellValues.length > 0;

        cellValues.forEach((cellValue, columnIdx) => {

            const importOption = this.getMappedImportOption(columnIdx);

            if (importOption) {
                importOption.isValid = this.validateField(importOption, cellValue, columnIdx, rowIdx, rowColTranslations);
                rowIsValid = rowIsValid && importOption.isValid;
            }
        });

        this.maxCols = cellValues.length > this.maxCols ? cellValues.length : this.maxCols;

        return rowIsValid;
    }

    private validateField(option: IImportOption, cellValue: string, colIdx: number, rowIdx: number, rowColTranslations: IRowCol[][]): boolean {
        if (this.csvImporterService.isEmptyCell(cellValue)) {
            if (option.isRequired) {
                this.csvImporterService.addValidationError(this.validationErrors, option.displayName, cellValue, this.translationService.t("csvImporter.fieldEmpty"), rowIdx, rowColTranslations, colIdx);
            }
            return !option.isRequired;
        }

        const validationResult = this.csvImporterService.isValidFieldForType(cellValue, option.fieldType);
        if (!validationResult.isValid) {
            this.csvImporterService.addValidationError(this.validationErrors, option.displayName, cellValue,
                this.csvImporterService.getInvalidFieldForTypeMessage(option, validationResult.message),
                rowIdx, rowColTranslations, colIdx);
            return false;
        }

        if (option.validate && !option.validate(cellValue)) {
            const validationText = option.validationText ? option.validationText : this.translationService.t("csvImporter.fieldInvalid");
            this.csvImporterService.addValidationError(this.validationErrors, option.displayName, cellValue, validationText, rowIdx, rowColTranslations, colIdx);
            return false;
        }

        return true;
    }

    private addOrUpdateValidationError(fieldName: string, fieldValue: string, validationText: string, rowIndex: number, colIndex?: number) {
        const colStr = colIndex || colIndex === 0 ? (colIndex + 1).toString() : "-";

        const currentError = {
            rowNumber: rowIndex + 1,
            colNumber: colStr,
            fieldName: fieldName,
            fieldValue: fieldValue,
            text: validationText
        };

        let inserted = false;
        for (let i = 0; i < this.validationErrors.length && !inserted; i++) {
            if (i > rowIndex) {
                this.validationErrors.splice(i, 0, currentError);
                inserted = true;
            }
        }

        if (!inserted) {
            this.validationErrors.push(currentError);
        }
    }

    private getColumnNames(maxCols: number): string[] {

        let alphabet = Array.from(Array(26).keys(), i => String.fromCharCode(i + 65));

        let offset = 0;

        while (alphabet.length < maxCols) {
            const firstChar = alphabet[offset++];
            alphabet =
                alphabet.concat(Array.from(Array(26).keys(), i => firstChar.concat(String.fromCharCode(i + 65))));
        }

        return alphabet;
    }

    private getDropdownColumnNumbers(maxCols: number) {
        const columnNames = this.getColumnNames(maxCols);

        this.columnImportNames = [];
        for (let i = 0; i < maxCols; i++) {
            this.columnImportNames.push(this.translationService.t('csvImporter.columnName', columnNames[i]));
        }
    }

    private getDropdownColumnNames(cellValues: string[]) {
        this.columnImportNames = [];
        cellValues.forEach(cellValue => {
            if (cellValue.trim().length > 0) {
                this.columnImportNames.push(cellValue);
            }
        });
    }

    private displayValidationErrors() {
        this.showValidationErrors = true;
        this.showMappings = false;
        this.showResults = false;
    }

    private setTitleOptions(csvLines: string[]) {
        const titleRowCellValues = this.csvImporterService.getTitleRowCellValues(csvLines);
        this.mappedImportOptions.forEach(option => {
            this.csvImporterService.mapImportOptionFromTitles(option, titleRowCellValues, this.validationErrors);
        });

        this.getDropdownColumnNames(titleRowCellValues);
    }

    private getMinStartOfWeekDate() {
        const today = new Date();
        const todayMoment = moment(today);
        return todayMoment.add(-today.getDay(), "days").toDate();
    }

}

export class CsvImporterComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            importConfig: "<",
            loading: "<",
            onCancel: "&",
            onSubmit: "&",
            onValidateRow: "&",
            onValidateData: "&",
            onUseAdapter: "&",
            onCallAdapter: "&"
        };

        this.controller = CsvImporterController;
        this.template = require("./csvImporter.html");
    }
}


